import React, { forwardRef } from "react";
import { Check as CheckIcon } from "@styled-icons/bootstrap";
import { useTranslation } from "react-i18next";
import styled, { css } from "styled-components/macro";

import InfoImage from "assets/images/wizard/WizardStepHostIntroductionPhoto.webp";
import { TWizardStepComponentStatic } from "../../../interfaces";
import { hexToRgba } from "../../../../../utils/common";

const Title = styled.div`
  font-size: 24px;
  font-weight: 500;
`;
const Intro = styled.div`
  color: ${(p) => p.theme.colors.midGrey};
  font-size: 18px;
  font-weight: 500;
  margin-bottom: 32px;
`;
const Content = styled.div`
  display: flex;
  gap: 100px;
`;
const StepsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const StepRoot = styled.div<{ $isCompleted: boolean; $isActive: boolean }>`
  ${(p) =>
    p.$isActive || p.$isCompleted
      ? css`
          background: white;
        `
      : ""};

  ${({ $isActive }) =>
    $isActive
      ? css`
          padding: 18px 24px;
        `
      : css`
          padding: 10px 24px;
        `};

  display: flex;
  gap: 24px;
  border-radius: 8px;
`;
const StepNumberContainer = styled.div`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;
const StepNumberBase = styled.div`
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const StepNumber = styled(StepNumberBase)`
  height: 24px;
  width: 24px;
  font-size: 14px;
  background: rgba(108, 108, 108, 0.1);
  color: ${(p) => hexToRgba(p.theme.colors.chatTextColor, 0.5)};
`;
const StepNumberCompleted = styled(StepNumberBase)`
  height: 24px;
  width: 24px;
  background: #ebf8e7;
  color: #32c51a;
`;
const StepNumberActive = styled(StepNumberBase)`
  height: 100%;
  width: 100%;
  background: ${(p) => hexToRgba(p.theme.colors.primary, 0.1)};
  color: ${(p) => p.theme.colors.primary};
`;
const StepInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
`;
const StepTitle = styled.div<{ $isActive: boolean }>`
  color: ${(p) => p.theme.colors.midGrey};
  ${(p) =>
    p.$isActive
      ? css`
          color: ${(p) => p.theme.colors.primary};
        `
      : ""}
  font-weight: 500;
`;
const StepDescription = styled.div`
  color: ${(p) => p.theme.colors.hardGray};
  font-weight: 500;
`;
const Image = styled.img`
  max-width: 450px;
  width: 100%;
  align-self: center;
`;

const Root = styled.div`
  @media (max-width: 768px) {
    height: 100%;
    padding-top: 40px;

    ${Content} {
      flex-wrap: wrap;
      gap: 32px;
      padding-bottom: 40px;
    }
  }
`;

const HostIntroductionStepsKeys = ["profile", "house", "publish"] as const;
type THostIntroductionStepKey = (typeof HostIntroductionStepsKeys)[number];

const steps: THostIntroductionStepKey[] = ["profile", "house", "publish"];

export const WizardStepIntroduction: TWizardStepComponentStatic<any> =
  forwardRef(() => {
    return <WizardStepIntroductionView activeStep="profile" />;
  });

export const WizardStepIntroductionView: React.FC<{
  activeStep: THostIntroductionStepKey;
}> = ({ activeStep }) => {
  const activeStepIndex = steps.findIndex((step) => step === activeStep);
  const { t } = useTranslation("host");

  return (
    <Root>
      <Title>
        {t(`wizard.steps.StepsIntroduction.substeps.${activeStep}.mainTitle`)}
      </Title>
      <Intro>
        {t(
          `wizard.steps.StepsIntroduction.substeps.${activeStep}.mainDescription`
        )}
      </Intro>
      <Content>
        <StepsList>
          {steps.map((step, index) => {
            return (
              <Step
                key={step}
                title={t(
                  `wizard.steps.StepsIntroduction.substeps.${step}.title`
                )}
                description={t(
                  `wizard.steps.StepsIntroduction.substeps.${step}.description`
                )}
                isActive={index === activeStepIndex}
                isCompleted={index < activeStepIndex}
                order={index + 1}
              />
            );
          })}
        </StepsList>
        <Image src={InfoImage} />
      </Content>
    </Root>
  );
};

export const Step: React.FC<{
  order: number;
  title: string;
  description: string;
  isCompleted: boolean;
  isActive: boolean;
}> = ({ title, description, order, isCompleted, isActive }) => {
  return (
    <StepRoot $isActive={isActive} $isCompleted={isCompleted}>
      <StepNumberContainer>
        {!isCompleted && !isActive ? <StepNumber>{order}</StepNumber> : null}
        {isActive ? <StepNumberActive>{order}</StepNumberActive> : null}
        {isCompleted ? (
          <StepNumberCompleted>
            <CheckIcon />
          </StepNumberCompleted>
        ) : null}
      </StepNumberContainer>
      <StepInfoContainer>
        <StepTitle $isActive={isActive}>{title}</StepTitle>
        {isActive ? <StepDescription>{description}</StepDescription> : null}
      </StepInfoContainer>
    </StepRoot>
  );
};
